/**
 * /wireline-slickline/jobs/:id — one job (GET / PATCH / DELETE
 * /wireline-slickline/jobs/:id, fieldiq-api#1338).
 *
 * Panels stack full width: details, the edit form when editing, the units on
 * the job with their pressure stacks, the rent-ins (dispatch and logistics
 * only), the day sheets, and the end-of-job service report (WL-3, api#1339:
 * create, or open the job's live report).
 *
 * STATUS MOVES ARE THE API'S. The buttons are `allowedTransitions` as the API
 * sent them; Cancel asks for the reason the API requires; Complete with a
 * draft sheet is refused (job_has_draft_sheets) and the sentence is shown.
 * A closed job (completed / cancelled) takes no edits (job_closed).
 *
 * Edit, status, delete, units, stacks and rent-ins are WL_DISPATCH. Opening a
 * day sheet is WL_SHEET_WRITE.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Activity, ClipboardList, Pencil, Trash2, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useWlMeta, useWlUsers, useWlWells } from '../../hooks/useWirelineSlickline';
import { useDeleteWlJob, useUpdateWlJob, useWlJob } from '../../hooks/useWlJobs';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { wlWellIdLabel, wlWellIdOf } from '../../lib/wireline-slickline';
import {
  canDispatchWl,
  canReadWlRentIns,
  canWriteWlSheets,
  WL_TRANSITION_LABEL,
  wlDay,
  wlJobRefusal,
  wlJobStatusSkin,
  wlStackRefusal,
  wlWarningText,
} from '../../lib/wireline-slickline-jobs';
import type { WlWarningEntry } from '../../types/wireline-slickline-jobs';
import { useBackToList } from '../downhole/recordPage';
import { JobForm } from './JobForm';
import { JobReportPanel } from './JobReportPanel';
import { JobRentInsPanel } from './JobRentInsPanel';
import { JobSheetsPanel } from './JobSheetsPanel';
import { JobUnitsPanel } from './JobUnitsPanel';
import {
  isNotFound,
  WL_BAND_SECONDARY,
  WL_EYEBROW,
  WL_INPUT,
  WL_PRIMARY,
  WL_SECONDARY,
  WL_SOUR_SKIN,
  WlBadge,
  WlDialog,
  WlEmpty,
  WlField,
  WlFormError,
  WlLoading,
  WlPanel,
  WlSpec,
  WlWarning,
} from './WlBits';

import { canReadWlTickets, canWriteWlTickets } from '../../lib/wireline-slickline-tickets';
import { WlJobTicketsSection, WlRentInBillingPanel } from './tickets/JobTicketSections';
import { WlJobInvoicesSection } from './invoices/InvoiceBits';
import { canUseWlAi } from '../../lib/wl-ai';
import { WlAiLauncher } from './ai/WlAiBits';

const OPEN = ['draft', 'scheduled', 'in_progress'];

export default function WlJobDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const back = useBackToList('/wireline-slickline/jobs');
  const { user } = useAuth();
  const query = useWlJob(id);
  const catalog = useWlMeta().data?.data ?? null;
  const canDispatch = canDispatchWl(user?.role);
  const job = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const open = !!job && OPEN.includes(job.status);
  const [editing, setEditing] = useState(false);
  const wells = useWlWells(undefined);
  const users = useWlUsers(canDispatch && editing);
  const update = useUpdateWlJob();
  const del = useDeleteWlJob();
  const [editError, setEditError] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<WlWarningEntry[]>([]);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    setEditing(false);
    setStatusError(null);
    setWarnings([]);
  }, [id]);

  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));
  const idLabel = wlWellIdLabel(meta);
  const wellId = job ? wlWellIdOf({ uwi: job.wellUwi, apiNumber: job.wellApiNumber }, meta) : null;

  const move = (to: string) => {
    setStatusError(null);
    setWarnings([]);
    update.mutate(
      { id: job!.id, body: { status: to } },
      {
        onSuccess: (res) => {
          toast.success(`Job ${res.data?.statusLabel?.toLowerCase() ?? 'updated'}`);
          setWarnings(res.warnings);
        },
        onError: (err) => setStatusError(wlStackRefusal(err)?.message ?? wlJobRefusal(err, 'The job status could not be changed.')),
      },
    );
  };

  return (
    <div data-testid="wl-job-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WL_EYEBROW}
        title={job?.jobNumber || (query.isLoading ? 'Job' : '—')}
        onBack={back}
        backLabel="Back"
        actions={
          job && canDispatch ? (
            <div className="flex flex-wrap gap-2">
              {open && (
                <button
                  type="button"
                  className="fi-action fi-action--standard fi-action--primary"
                  onClick={() => { setEditError(null); setEditing(true); }}
                  disabled={editing}
                >
                  <Pencil className="size-4" aria-hidden="true" />
                  Edit job
                </button>
              )}
              {job.allowedTransitions.map((to) => (
                <button
                  key={to}
                  type="button"
                  className={WL_BAND_SECONDARY}
                  data-testid={`wl-job-move-${to}`}
                  disabled={update.isPending}
                  onClick={() => {
                    if (to === 'cancelled') {
                      setCancelError(null);
                      setCancelReason('');
                      setCancelling(true);
                    } else move(to);
                  }}
                >
                  {WL_TRANSITION_LABEL[to] ?? to}
                </button>
              ))}
              {(job.status === 'draft' || job.status === 'scheduled') && (
                <button type="button" className={WL_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                  <Trash2 className="size-4" aria-hidden="true" />
                  Delete
                </button>
              )}
            </div>
          ) : undefined
        }
        stats={[
          {
            icon: <Activity className="size-4" aria-hidden="true" />,
            label: 'Status',
            tone: 'neutral',
            loading: query.isLoading,
            value: job ? job.statusLabel ?? wlJobStatusSkin(job.status).label : '—',
          },
          { icon: <Truck className="size-4" aria-hidden="true" />, label: 'Units', tone: 'neutral', loading: query.isLoading, value: displayValue(job?.unitCount) },
          { icon: <ClipboardList className="size-4" aria-hidden="true" />, label: 'Sheets', tone: 'accent', loading: query.isLoading, value: displayValue(job?.sheetCount) },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <WlLoading label="Loading job" />
          ) : notFound || (!failure && !job) ? (
            <WlPanel eyebrow="Job" title="Job">
              <WlEmpty>This job was not found. It may have been deleted, or you are not on its crew.</WlEmpty>
            </WlPanel>
          ) : failure || !job ? (
            <WlPanel eyebrow="Job" title="Job">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this job" />
            </WlPanel>
          ) : (
            <>
              <WlFormError message={statusError} />
              {warnings.map((w, i) => (
                <WlWarning key={i} message={wlWarningText(w)} testId="wl-job-warning" />
              ))}
              <WlPanel eyebrow="Job" title="Details" testId="wl-job-details">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <WlSpec label="Status" value={<WlBadge skin={wlJobStatusSkin(job.status, job.statusLabel)} value={job.status} />} />
                  <WlSpec label="Customer" value={job.customerName || '—'} />
                  <WlSpec
                    label="Well"
                    value={
                      <span className="inline-flex items-center gap-2">
                        {job.wellName || '—'}
                        {job.wellSour && <WlBadge skin={WL_SOUR_SKIN} value="sour" />}
                      </span>
                    }
                  />
                  <WlSpec label={idLabel} value={wellId || '—'} mono />
                  <WlSpec label="Field" value={job.wellFieldName || '—'} />
                  <WlSpec label="Run kinds" value={job.scopeLabels?.length ? job.scopeLabels.join(', ') : '—'} />
                  <WlSpec label="Supervisor" value={job.supervisorName || '—'} />
                  <WlSpec label="Ticket mode" value={(job.ticketMode && catalog?.ticketModes?.find((m) => m.value === job.ticketMode)?.label) || job.ticketMode || '—'} />
                  <WlSpec label="Planned" value={`${wlDay(job.plannedStartOn)} – ${wlDay(job.plannedEndOn)}`} />
                  <WlSpec label="PO number" value={job.poNumber || '—'} mono />
                  <WlSpec label="AFE number" value={job.afeNumber || '—'} mono />
                  <WlSpec label="Cost code" value={job.costCode || '—'} mono />
                </dl>
                {(job.description || job.notes || job.cancelReason) && (
                  <div className="space-y-1 border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">
                    {job.description && <p>{job.description}</p>}
                    {job.notes && <p className="text-fieldiq-muted">{job.notes}</p>}
                    {job.cancelReason && <p>Cancelled: {job.cancelReason}</p>}
                  </div>
                )}
              </WlPanel>

              {editing && canDispatch && open && (
                <WlPanel eyebrow="Edit" title="Edit job" testId="wl-job-edit">
                  <JobForm
                    key={job.id}
                    job={job}
                    catalog={catalog}
                    wells={(wells.data?.data ?? []).map((w) => ({
                      id: w.id,
                      wellName: w.wellName,
                      customerName: w.customerName,
                      idText: wlWellIdOf(w, meta),
                    }))}
                    users={users.data ?? []}
                    pending={update.isPending}
                    error={editError}
                    submitLabel="Save job"
                    onCancel={() => setEditing(false)}
                    onSubmit={(body) => {
                      setEditError(null);
                      // The well is sent only when it changed: re-sending it re-asks the well/customer pair.
                      const out = { ...body };
                      if (out.well_id === job.wellId) delete out.well_id;
                      update.mutate(
                        { id: job.id, body: out },
                        {
                          onSuccess: (res) => {
                            toast.success('Job saved');
                            setWarnings(res.warnings);
                            setEditing(false);
                          },
                          onError: (err) => setEditError(wlStackRefusal(err)?.message ?? wlJobRefusal(err, 'The job could not be saved.')),
                        },
                      );
                    }}
                  />
                </WlPanel>
              )}

              <JobUnitsPanel job={job} canWrite={canDispatch} open={open} />
              {canReadWlRentIns(user?.role) && job.rentIns && <JobRentInsPanel job={job} catalog={catalog} canWrite={canDispatch} open={open} />}
              {canReadWlTickets(user?.role) && job.rentIns && job.rentIns.length > 0 && (
                <WlRentInBillingPanel job={job} rentIns={job.rentIns} canWrite={canWriteWlTickets(user?.role)} />
              )}
              <JobSheetsPanel job={job} canOpen={canWriteWlSheets(user?.role)} today={meta?.today ?? null} />
              {canReadWlTickets(user?.role) && <WlJobTicketsSection job={job} canWrite={canWriteWlTickets(user?.role)} today={meta?.today ?? null} />}
              {canReadWlTickets(user?.role) && <WlJobInvoicesSection jobId={job.id} />}
              {canUseWlAi(user?.role) && (
                <WlAiLauncher
                  key={job.id}
                  request={{ agent: 'wl_ticket_preflight', jobId: job.id }}
                  eyebrow="Before sending"
                  title="Job preflight"
                  blurb="Checks this job's open tickets and approved day sheets for unpriced lines, sheets on no ticket, and run kinds with no rate. The AI's text explains the flags; it changes nothing."
                  testId="wl-ai-job-preflight"
                />
              )}
              <JobReportPanel job={job} canWrite={canDispatch} timeZone={meta?.timeZone ?? null} />
            </>
          )}
        </div>
      </section>

      {job && (
        <WlDialog
          open={cancelling}
          title={`Cancel ${job.jobNumber}`}
          onClose={() => setCancelling(false)}
          footer={
            <>
              <button type="button" className={WL_SECONDARY} onClick={() => setCancelling(false)} disabled={update.isPending}>
                Keep job
              </button>
              <button
                type="button"
                className={WL_PRIMARY}
                disabled={update.isPending || cancelReason.trim() === ''}
                onClick={() =>
                  update.mutate(
                    { id: job.id, body: { status: 'cancelled', cancel_reason: cancelReason.trim() } },
                    {
                      onSuccess: () => {
                        toast.success('Job cancelled');
                        setCancelling(false);
                      },
                      onError: (err) => setCancelError(wlJobRefusal(err, 'The job could not be cancelled.')),
                    },
                  )
                }
              >
                {update.isPending ? 'Cancelling…' : 'Cancel job'}
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <WlFormError message={cancelError} />
            <WlField label="Reason" hint="Kept on the job">
              <textarea className={`${WL_INPUT} h-24 py-2`} aria-label="Cancel reason" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
            </WlField>
          </div>
        </WlDialog>
      )}

      {job && (
        <WlDialog
          open={deleting}
          title={`Delete ${job.jobNumber}`}
          onClose={() => setDeleting(false)}
          footer={
            <>
              <button type="button" className={WL_SECONDARY} onClick={() => setDeleting(false)} disabled={del.isPending}>
                Keep job
              </button>
              <button
                type="button"
                className={WL_PRIMARY}
                disabled={del.isPending}
                onClick={() =>
                  del.mutate(job.id, {
                    onSuccess: () => {
                      toast.success('Job deleted');
                      navigate('/wireline-slickline/jobs', { replace: true });
                    },
                    onError: (err) => setDeleteError(wlJobRefusal(err, 'The job could not be deleted.')),
                  })
                }
              >
                {del.isPending ? 'Deleting…' : 'Delete job'}
              </button>
            </>
          }
        >
          <WlFormError message={deleteError} />
          <p className="text-sm text-fieldiq-text">A draft or scheduled job with no day sheets can be deleted. Otherwise cancel it.</p>
        </WlDialog>
      )}
    </div>
  );
}
